<script setup lang="ts">
/**
 * The one modal shell for the admin panel, built once and reused.
 *
 * No modal existed anywhere in `frontend/src/admin/` before this — every
 * create/edit affordance was an inline table-row toggle or an in-place form
 * swap. Four features need a genuine modal (Legal, News, FAQ, +Add user), so
 * this is the shared shell rather than four bespoke ones.
 *
 * The scrim/window/focus-on-mount/Escape-to-close shape is copied from
 * `garage/ConvertCreditsModal.vue` — same reasoning as that file's own header:
 * a thing you look up, act on and close again. It is copied rather than
 * imported because that file is garage-scoped (its own `--steel-*`/`--accent`
 * tokens happen to be the same global tokens `pane.css` uses, but the two
 * screens should not have to agree on that by coincidence). This one uses
 * `pane.css`'s admin chrome directly — `.admin-main` classes work here because
 * the panel mounts this component inside its own `.admin-main` tree.
 */
import { onMounted, ref } from 'vue';

defineProps<{
  title: string;
  /** Widens the window for content that needs more room — a rich-text editor. */
  wide?: boolean;
}>();

const emit = defineEmits<{ close: [] }>();

const dialog = ref<HTMLElement | null>(null);
onMounted(() => dialog.value?.focus());

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation();
    emit('close');
  }
}
</script>

<template>
  <div class="admin-main scrim" @click.self="emit('close')">
    <section
      ref="dialog"
      class="window"
      :class="{ wide }"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <header>
        <h2>{{ title }}</h2>
        <button type="button" class="close" @click="emit('close')">Close</button>
      </header>

      <div class="body">
        <slot />
      </div>

      <footer v-if="$slots.footer">
        <slot name="footer" />
      </footer>
    </section>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(6, 8, 11, 0.72);
  z-index: 50;
}

.window {
  width: min(30rem, 100%);
  max-height: min(85vh, 42rem);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 1.1rem 1.2rem 1.2rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.4rem;
  background: var(--steel-900);
}

.window.wide {
  width: min(46rem, 100%);
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex: none;
}

h2 {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.05rem;
  letter-spacing: 0.02em;
  color: var(--steel-200);
}

.close {
  flex: none;
  min-height: 1.7rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.2rem;
  background: none;
  color: var(--steel-400);
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.close:hover {
  border-color: var(--accent-dim);
  color: #fff;
}

.body {
  overflow-y: auto;
  min-height: 0;
}

footer {
  flex: none;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
